<template>
  <div
    class="edit-program-steps g-edit-component"
    :class="[GetStepClass, {'show-edit-stations': ShowEditStations}]"
  >
    <!-- Wrapper -->
    <div
      v-if="error"
      class="edit-program-steps__error"
    >
      <h5>Error #sdlist</h5>
      <p>
        {{ $t('messages.programEdit.setRunTimes.noStations') }}
      </p>
      <div class="edit-container__error__action">
        <button
          class="g-btn g-btn--rounded"
          @click.stop="reload()"
        >
          Reload Application
        </button>
      </div>
      <small>
        If the problem persists please contact your administrator
      </small>
    </div>
    <div
      v-else
      class="edit-program-steps-wrapper"
    >
      <!-- Header -->
      <slot v-if="Steps.length">
        <slot v-if="ModalData.capabilities.steps.maxStationsPerStep === 1">
          <h1 class="g-title">
            <p>
              <span v-if="Steps.length">
                {{ $t('messages.programEdit.setRunTimes.overview', {
                  groups: Steps.length,
                  groupOrSteps: $tc('messages.sharedTexts.group', 0),
                  time: TotalRuntime,
                  water: computedTotalWaterUsage,
                  waterUnit: getDenominator('litre', 'gallon', 2),
                  waterBudget: waterBudgetPercent
                }) }}
              </span>
              <small>
                <br>
                <i>* {{ $t('messages.programEdit.setRunTimes.overviewNote') }}</i>
              </small>
            </p>
          </h1>
        </slot>
        <slot v-else>
          <h1 class="g-title">
            <p>
              <span v-if="Steps.length">
                {{ $t('messages.programEdit.setRunTimes.overview', {
                  groups: Steps.length,
                  groupOrSteps: $tc('messages.sharedTexts.step', 0),
                  time: TotalRuntime,
                  water: computedTotalWaterUsage,
                  waterUnit: getDenominator('litre', 'gallon', 2),
                  waterBudget: waterBudgetPercent
                }) }}
              </span>
            </p>
          </h1>
        </slot>
      </slot>
      <!-- "Table" Header -->
      <ol
        v-if="Steps.length !== 0"
        class="step-list step-list--header"
      >
        <li class="step-list__step step-list__step--step step-list__step--title">
          {{ $tc('messages.sharedTexts.step', 1) }}
        </li>
        <li
          title="Click below to change Runtime"
          class="step-list__step step-list__step--runtime step-list__step--title"
        >
          {{ $t('messages.programEdit.setRunTimes.runtime') }}
        </li>
        <li class="step-list__step step-list__step--flow step-list__step--title">
          {{ $t('messages.programEdit.setRunTimes.flow.title') }}
        </li>
        <li class="step-list__step step-list__step--stations step-list__step--title">
          {{ $t('messages.programEdit.setRunTimes.stationsIncluded') }}
        </li>
        <li class="step-list__step step-list__step--stations-edit step-list__step--title--small">
          {{ $t('messages.programEdit.setRunTimes.selectStations') }}
        </li>
        <li class="step-list__step step-list__step--step-add-above step-list__step--title--small">
          Add step<br>above
        </li>
        <li class="step-list__step step-list__step--step-delete step-list__step--title--small">
          <p>
            {{ $t('messages.programEdit.setRunTimes.deleteGroup', {
              groupOrStep: ModalData.capabilities.steps.maxStationsPerStep !== 1 ? $tc('messages.sharedTexts.step', 1) : $tc('messages.sharedTexts.group', 1)
            }) }}
          </p>
        </li>
      </ol>
      <p
        v-else
        class="g-title g-title--subtitle"
      >
        {{ $t('messages.programEdit.setRunTimes.noRuntimes') }}
      </p>
      <slot v-for="(step, index) in Steps">
        <ol
          :key="index"
          class="step-list step-list--item"
        >
          <!-- index -->
          <li class="step-list__step step-list__step--step">
            <span class="mobile-only">
              {{ $tc('messages.sharedTexts.step', 1) }}&nbsp;
            </span>{{ index + 1 }}
          </li>
          <!-- Runtime -->
          <li class="step-list__step step-list__step--runtime">
            <span class="mobile-only">
              {{ $t('messages.programEdit.setRunTimes.runtime') }}
            </span>
            <timepicker-element
              v-bind="{Data: step.timepickerElement}"
              @change="RuntimeChange(step)"
            />
          </li>
          <!-- Flow -->
          <li class="step-list__step step-list__step--flow">
            <div
              class="step-list__step__flow"
              :class="['step-list__step__flow--' + (GetFlow(step).className)]"
              :title="GetFlow(step).status"
            >
              {{ convertUnits(GetFlow(step).flow, 'gal/min', 'l/min') }}
            </div>
            <span class="mobile-only">
              {{ getDenominator('litre', 'gallon', 0) + '/' + $tc('messages.sharedTexts.time.m', 1) }}
            </span>
          </li>
          <!-- Stations -->
          <li class="step-list__step step-list__step--stations">
            <slider-element
              v-if="step && step.decoders && step.decoders.length"
              class="step-list__step__stations slider-element--no-borders slider-element--no-background"
              v-bind="{'Data': step.sliderData }"
            >
              <button
                v-for="station in step.decoders"
                :key="station ? station.stId : Math.random()"
                class="g-btn g-btn--station"
                :class="{'g-btn--station--active-border': step.sliderData.elementPosition === index}"
              >
                <span class="g-btn--station__text">
                  {{ station ? station.name : '' }}
                </span>
              </button>
            </slider-element>
          </li>
          <!-- Step: Edit Stations -->
          <li class="step-list__step  step-list__step--edit">
            <button
              class="step-list__step__btn g-btn g-btn--round"
              @click="ToggleShowEditStations(index, step.decoders, step.runtime)"
            >
              <font-awesome-icon :icon="['fas', 'edit']" />
            </button>
          </li>
          <!-- Step: Add Above -->
          <li class="step-list__step  step-list__step--step-add-above">
            <button
              class="step-list__step__btn g-btn g-btn--round"
              @click="ClickAddAbove(index)"
            >
              <font-awesome-icon :icon="['fas', 'upload']" />
            </button>
          </li>
          <!-- Step: Delete -->
          <li class="step-list__step  step-list__step--step-delete">
            <button
              class="step-list__step__btn g-btn g-btn--round"
              @click="ClickDeleteStep(index)"
            >
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </li>
        </ol>
      </slot>
      
      <!-- simulate a step for further addition -->
      <ol class="step-list step-list--item step-list--add-step">
        <!-- Add new Step: Add Button -->
        <li class="step-list__step">
          <button
            v-if="ModalData.capabilities.steps.maxStationsPerStep === 1"
            class="g-btn g-btn--rounded"
            @click="AddNewStepClick()"
          >
            <slot v-if="Steps.length === 0">
              {{ $t('messages.programEdit.setRunTimes.runtime') }}
            </slot>
            <slot v-else>
              {{ $t('messages.programEdit.setRunTimes.addNewRuntimeRow') }}
            </slot>
          </button>
          <button
            v-else-if="Steps.length <= 99"
            class="g-btn g-btn--rounded"
            @click="AddNewStepClick()"
          >
            {{ $t('messages.programEdit.setRunTimes.addNewStep') }}
          </button>
        </li>
      </ol>
    </div>
    <!-- remove v-show to re-instate animation -->
    <edit-program-stations
      v-show="ShowEditStations"
      v-bind="{StationsData, Current, ModalData, systemCapacity: store.getData('systemCapacity')}"
      :store="store"
      @ToggleShowEditStationsFromChild="ToggleShowEditStationsFromChild"
    />
  </div>
</template>

<script>
/* Mixins */
import {SocketHandler, LifeCycle, UnitSystem} from '@/assets/javascript/mixins/Mixins'
/* Libraries */
import {secondsToObj} from '@/assets/javascript/Time-lib'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
/* Component Elements */
import SliderElement from '@/components/elements/SliderElement.vue'
import TimepickerElement from '@/components/elements/TimepickerElement.vue'
/* components */
import EditProgramStations from '@/components/EditProgramStations.vue'

export default {
  name: 'EditProgramRunTimes',
  components:
  {
    'font-awesome-icon': FontAwesomeIcon,
    'slider-element': SliderElement,
    'timepicker-element': TimepickerElement,
    'edit-program-stations': EditProgramStations
  },
  mixins: [SocketHandler, LifeCycle, UnitSystem],
  props: {
    SocketMessage: {
      type: Object,
      default() {
        return{}
      }
    },
    PageMessage: {
      type: Object,
      default() {
        return{}
      }
    },
    ModalData: {
      type: Object,
      default() {
        return{}
      }
    },
    StationsData: {
      type: Array,
      default() {
        return []
      }
    },
    store: {
      type: Object,
      default: function() {
        return{}
      }
    }
  },
  data() {
    return{
      error: null,
      TotalRuntime: 0,
      waterBudgetPercent: 0,
      MaxSteps: 15,
      SliderData: {
        elementPosition: 0
      },
      Steps: [],
      ShowEditStations: false,
      Current: {
        currentStations: [],
        currentRow: NaN,
        runtime: 0
      },
    }
  },
  computed: {
    computedTotalWaterUsage() {
      let waterAmount = null
      let wbWaterAmount = 0
      let rtn = 0
      if(this.Steps.length) {
        waterAmount = this.Steps.map(step => // Create new array for each step that contains
          step.decoders.map(decoder => // each decoder waterAmount used based on runtime
            decoder && decoder.expectedFlow ?  decoder.expectedFlow * step.runtime / 60 : 0)
          )
          .flat() // then flatten the array as we'll have n arrays where n is steps
          .reduce((a, b) => a + b, 0) // find the sum of the results, 0 is default value
        
        waterAmount = this.convertUnits(waterAmount, 'gal', 'l')
        wbWaterAmount = waterAmount / 100 * this.waterBudgetPercent
        rtn = wbWaterAmount > 1000 ? `${(wbWaterAmount / 1000).toFixed(2)}K` : wbWaterAmount.toFixed(2)
      }

      return rtn  
    },
    GetStepClass() {
      const bool = this.ModalData.capabilities.steps.maxStationsPerStep === 1
      const returnClass = 'edit-program-steps--' + (bool ? 'single' : 'steps')
      return returnClass
    },
    CurrentDecodersInUse() { // Go through steps, get decoder objs and return the id, the concat all the arrays to one
      return [].concat.apply([], this.Steps.map(step => step.decoders.map(decoder => decoder.stId)))
    }
  },
  watch: {
    StationsData: {
      handler(newVal) {
        if(this.ModalData.steps && this.ModalData.steps.length && this.ModalData.steps[0] !== '') {
          // console.log('stationsdata update', this.ModalData.steps)
          this.Steps = this._GetSteps(this.ModalData.steps, true)
        }
      },
      immediate: true
    },
    // Careful here, it updates for every change, init as well
    Steps: {
      handler(newVal) {
        this.TotalTime = this._GetTotalRuntime()
        /** Be aware that splitting Steps from ModalData.steps doesn't
        make sense if we just pass them back again. **/
        const data = JSON.parse(JSON.stringify(this.Steps))
        this.ModalData.steps = data !== '' ? data : []
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.Steps.forEach((item) => {
      if(isNaN(item.timepickerElement.hh) || isNaN(item.timepickerElement.mm) || isNaN(item.timepickerElement.ss)) {
        item.timepickerElement.hh = '00'
        item.timepickerElement.mm = '00'
        item.timepickerElement.ss = '00'
      }
    })
  },
  mounted () {
    this.SocketMessageSend({
      component: 'Programs',
      command: 'Mounted'
    })
  },
  methods: {
    RuntimeChange(step) {
      const check = this.ModalData.capabilities.steps.maxStationsPerStep === 1 // Only 1 Station per Step
      if(check) {
        this._SendDecoders(step.runtime, step.decoders, 'Upd', false)
      } else {
        this._SendStep(step.runtime, step.stepNo, 'Upd')
      }
    },
    GetFlow(stepData) {
      // Get concat flow from Step
      let flowArray = [].concat.apply([], stepData.decoders.map(step => step ? step.expectedFlow : 0))
      let flow = 0

      flowArray.forEach((currentFlow) => {
        flow += (currentFlow !== '') ? parseInt(currentFlow) : 0
      })

      // Calculate status class
      let className = ''
      let status = ''

      if(this.store.getData('systemCapacity') !== 0 || flow !== 0) {
        const ratio = (flow / this.store.getData('systemCapacity')) * 100

        if(ratio > 0 && ratio < 10) {
          className = 'grey'
          status = this.$t('messages.programEdit.setRunTimes.flow.grey')
        } else if(ratio >= 10 && ratio <= 50) {
          className = 'yellow'
          status = this.$t('messages.programEdit.setRunTimes.flow.yellow')
        } else if(ratio >= 50 && ratio <= 75) {
          className = 'green'
          status = this.$t('messages.programEdit.setRunTimes.flow.green')
        } else if(ratio >= 75 && ratio <= 100) {
          className = 'blue'
          status = this.$t('messages.programEdit.setRunTimes.flow.blue')
        } else {
          className = 'red'
          status = this.$t('messages.programEdit.setRunTimes.flow.red')
        }
      }

      return {
        flow,
        className,
        status
      }
    },
    ToggleShowEditStationsFromChild(current) {
      // console.log('current.currentRow: ' + current.currentRow)
      // for (i = 0; i < this.Steps.length; i++) {
      //   console.log('this.Steps[' + (i) + '].stepNo: ' + this.Steps[i].stepNo)
      // }

      // console.log(JSON.stringify(this.Steps))

      let nulledArray = current.currentStations.slice(0)
      let addArray = []
      let delArray = []
      const steps = this.Steps[current.currentRow].decoders
      const runtime = this.Steps[current.currentRow].runtime
      const stepNo = this.Steps[current.currentRow].stepNo  // this can be undefined when adding things to the RunStep list *without* a stepNo field. fix in commit  
      
      // RKX
      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        current.change.forEach(newStationData => {
          const test = steps.find(currentStationData => currentStationData.stId === (newStationData.stId))
          if(test === undefined && newStationData.selected !== false) {
            addArray.push(newStationData)
          } else if(newStationData.selected === false) {
            delArray.push(newStationData)
          }
        })
        // ORDER MATTERS

        this._SendDecoders(runtime, delArray, 'Del', stepNo, false)
        this.Steps[current.currentRow].decoders = current.currentStations
        this._SendDecoders(runtime, addArray, 'Add', stepNo, false)
      } else { // TWC
        if(current.currentStations.length >= steps.length) { // New is larger, update
          this._SendDecoders(runtime, current.currentStations, 'Upd', stepNo, false)
          // console.log('current.currentRow is: '+ current.currentRow + ', stepNo is now: ' + stepNo)
        } else { // New is smaller, null the difference
          for( var i = 0; i <= steps.length - current.currentStations.length; i++ ) {
            nulledArray.push(null)
          }
          this._SendDecoders(runtime, nulledArray, 'Upd', stepNo, false)
        }
      }

      this.ShowEditStations = !this.ShowEditStations
      this.Steps[current.currentRow].decoders = current.currentStations
    },
    ToggleShowEditStations(index, decoders, runtime) {
      this.store.setData('editProgramsLocked', true)
      
      runtime = secondsToObj(runtime)
      
      const hours = (runtime.hh !== 0) ? runtime.hh + this.$tc('messages.sharedTexts.time.h', 1) : ''
      const minutes = (runtime.mm !== 0) ? runtime.mm + this.$tc('messages.sharedTexts.time.m', 1) : ''
      const seconds = (runtime.ss !== 0) ? runtime.ss + this.$tc('messages.sharedTexts.time.s', 1) : ''
      runtime = `${hours} ${minutes} ${seconds}`

      let currentInUse = []

      if(this.ModalData.capabilities.steps.maxStationsPerStep === 1) {
        currentInUse = this.CurrentDecodersInUse.filter((el) => !decoders.map(decoder => decoder.stId).includes(el))
      }
      this.Current = {
        currentStations: decoders,
        currentRow: index,
        currentInUse: currentInUse,
        runtime
      }
      this.ShowEditStations = !this.ShowEditStations
    },
    AddNewStepClick() {
      this.Steps.push({
        stepNo: this.Steps.length + 1,  // this was missing(?), sometimes causing ToggleShowEditStationsFromChild() to return an undefined value if there was no stepNo in the record - jimm 01.feb.2021
        selected: false,
        sliderData: Object.assign({}, this.SliderData),
        // We only have the StID so we need to look up in the station list to get the rest of the data
        decoders: [],
        timepickerElement: Object.assign({}, this._GetRuntime(0)),
        runtime: 0
      })

      if(this.ModalData.capabilities.steps.maxStationsPerStep >= 1) {
        this._SendStep(0, this.Steps.length, 'Add')  // null was not giving enough context (see next line) -jimm/jonas 01.feb.2021
        // this._SendStep(0, null, 'Add')
        // (or should above be this.Steps.stepNo instead of this.Steps.length?  (test)
      }
    },
    ClickAddAbove(index) {
      let step = this.Steps[index]

      this.Steps.splice(index, 0, {
        selected: false,
        sliderData: Object.assign({}, this.SliderData),
        // We only have the StID so we need to look up in the station list to get the rest of the data
        decoders: [],
        timepickerElement: Object.assign({}, this._GetRuntime(0)),
        runtime: 0
      })

      this.Steps.forEach((item, itemIndex) => {
        this.Steps[itemIndex].stepNo = itemIndex + 1
      })

      if(this.ModalData.capabilities.steps.maxStationsPerStep >= 1) { // Multiple Decoders per step
        this._SendStep( step.runtime, step.stepNo, 'Ins')
        this._SendStep( 0, step.stepNo - 1, 'Upd')
        this._SendDecoders(step.runtime, step.decoders, 'Upd', step.stepNo, false)
        this._SendDecoders(step.runtime, step.decoders, 'Upd', step.stepNo - 1, true)
      }
    },
    ClickDeleteStep(index) {
      let step = this.Steps[index]   // here-in lies part of the problem, referencing by index *but* sending stepNo for deletion; local list is not renumbered -jimm 2.feb

      this.Steps.splice(index, 1)  // remove entry from list (original code by Thomas)
      
      this.Steps.forEach((item, reIndex) => {
        this.Steps[reIndex].stepNo = reIndex + 1
      })
      
      if(this.ModalData.capabilities.steps.maxStationsPerStep !== 1) { // Multiple Decoders per step
        this._SendStep(step.runtime, step.stepNo, 'Del')
      } else {
        this._SendDecoders(0, step.decoders, 'Upd', null,  false)
      }
    },
    /** @returns: Array containing Objects acting as step rows for TWC & RK* */
    _GetSteps(stepsArray, collate) {
      // console.log('stepsarray ', JSON.stringify(stepsArray))
      this.Steps = []
      /** @param decoderList: Array of decoders */
      const _getDecoderInformation = (decoderList) => {
        return decoderList.map((stationID) => {
          if(typeof stationID === 'object' && stationID !== null) {
            stationID = stationID.stId
          }

          if(this.StationsData.length) {
            /* Note: you can get ALL data for the station if you want, just return stationFiltered */
            const stationFiltered = this.StationsData.filter((station) => station.stId === stationID)[0]

            if(stationFiltered) {
              return{
                stId: stationID,
                name: stationFiltered.name,
                expectedFlow: stationFiltered.expectedFlow
              }
            } else {
              this.error = true
              console.error('Cannot show steps.')
              return {}
            }
          }
        })
      }

      const stepList = []
      stepsArray.forEach((step, index) => {
        // Returns an object with all the step data in it
        // Matching - update (RK*) Grouping by Runtime
        if(this.ModalData.capabilities.steps.maxStationsPerStep === 1 && collate) {
          let matchingStep = stepList.filter((stepListStep) => step.runtime === stepListStep.runtime)
          if(matchingStep && matchingStep.length && matchingStep[0].runtime === step.runtime) {
            matchingStep = matchingStep[0]
            matchingStep.decoders = matchingStep.decoders.concat(_getDecoderInformation(step.decoders))
            return
          }
        }

        // Not matching or TWC - create
        const stepData = Object.assign({}, step, {
          selected: false,
          sliderData: Object.assign({}, this.SliderData),
          // We only have the StID so we need to look up in the station list to get the rest of the data
          decoders: _getDecoderInformation(step.decoders)
        })

        this.$set(stepData, 'timepickerElement', this._GetRuntime(step.runtime || step.runTime))

        stepList.push(stepData)
      })
      // this._SetTotalRuntime();
      return stepList
    },
    _GetTotalRuntime() {
      let totalRuntime = 0
      let totalTime = 0
      let wb = 0
      let prgNr = 0
      this.Steps.forEach((step) => {
        let temp = 0
        temp += parseInt(step.timepickerElement.hh, 10) * 3600
        temp += parseInt(step.timepickerElement.mm, 10) * 60
        temp += parseInt(step.timepickerElement.ss, 10)

        if(isNaN(temp)) temp = 0

        totalRuntime += this.ModalData.capabilities.steps.maxStationsPerStep === 1
        ? temp * step.decoders.length
        : temp

        step.runtime = temp
      })
      prgNr = this.ModalData.ID -1
      this.waterBudgetPercent = this.store.data.programs[prgNr].waterBudget
      totalTime = totalRuntime / 100 * this.waterBudgetPercent
      totalRuntime = secondsToObj(totalTime)
      
      const hours = (totalRuntime.hh !== 0) ? totalRuntime.hh + this.$tc('messages.sharedTexts.time.h', 1) : ''
      const minutes = (totalRuntime.mm !== 0) ? totalRuntime.mm + this.$tc('messages.sharedTexts.time.m', 1) : ''
      const seconds = (totalRuntime.ss !== 0) ? totalRuntime.ss + this.$tc('messages.sharedTexts.time.s', 1) : ''
      totalRuntime = `${hours} ${minutes} ${seconds}`
      this.TotalRuntime = totalRuntime
    },
    _GetRuntime(time) {
      const CreateRuntime = () => {
        let time = {
          hh: 0,
          mm: 0,
          ss: 0,
          hhMin: 0,
          hhMax: 17,
          mmMin: 0,
          mmMax: 59,
          ssMin: 0,
          ssMax: 59
        }

        if(this.ModalData.capabilities.steps.maxStationsPerStep > 1) {
          time.hide = [
            'ss'
          ]
        }
        return time
      }
      let runtime = new CreateRuntime()

      const timeObj = secondsToObj(time)

      runtime.hh = timeObj.hhString
      runtime.mm = timeObj.mmString
      runtime.ss = timeObj.ssString
      return runtime
    },
    _SendStep(Runtime, StepID, Oper) {
      let msg = {}
      if(this.ModalData.capabilities.steps.maxStationsPerStep > 1) {  //  Multiple Decoders per step
        msg = {
          Category: 'Irrigation',
          Type: 'IrrPgmCont',
          Oper,
          PgmId: parseInt(this.ModalData.ID),
          Seq: (StepID === undefined) ? (this.Steps.length) : StepID,
          RunTime: (Oper === 'Del') ? 0 : Number(Runtime)
        }
        this.SocketMessageSend(msg)
      } else { // Decoders grouped on runtime
        msg = {
          Category: 'Irrigation',
          Type: 'IrrPgmCont',
          Oper,
          PgmId: parseInt(this.ModalData.ID),
          Seq: parseInt(StepID),
          RunTime: (Oper === 'Del') ? 0 : parseInt(Runtime)
        }
        this.SocketMessageSend(msg)
      }
    },
    _SendDecoders(Runtime, Decoders, Oper, StepNo, AddStepAbove) {
      this.store.setData('editProgramsLocked', false)

      let msg = {}
      if(Decoders && Decoders.length !== 0) {
        let maxStepCheck = (this.ModalData.capabilities.steps.maxStationsPerStep === 1)
        if(!maxStepCheck) { // More than 1 Station per Step allowed
          Decoders.forEach(
            (obj, index) => {
              msg = {
                Category: 'Irrigation',
                Type: 'IrrPgmStep',
                Oper: 'Upd',
                PgmId: parseInt(this.ModalData.ID),
                StepNo: (StepNo === undefined) ? (this.Steps.length) : parseInt(StepNo),
                Station: (obj && Object && !AddStepAbove) ? obj.stId : null,
                Seq: (index + 1)
              }
              this.SocketMessageSend(msg)
            }
          )
        } else {                                  // Only 1 Station per Step allowed
          Decoders.forEach((obj, index) => {
            msg = {
              Category: 'Irrigation',
              Type: (maxStepCheck) ? 'IrrPgmCont' : 'IrrPgmStep',
              Oper: (maxStepCheck) ? 'Upd' : Oper,
              PgmId: parseInt(this.ModalData.ID),
              RunTime: (Oper === 'Del') ? 0 : parseInt(Runtime),
              Seq: parseInt(obj.stId)
            }
            this.SocketMessageSend(msg)
          })
        }
      } else {
        // console.warn('no decoders to work with');
      }   
    },
    ExportSteps(Steps) { // Sequences decoders with runtime
      let exportArray = []
      Steps.forEach((step) => {
        const runtime = String(parseInt(step.timepickerElement.hh) * 3600 + parseInt(step.timepickerElement.mm) * 60 + parseInt(step.timepickerElement.ss))
        if(step.decoders.length > 1) {
          step.decoders.forEach((decoder) => {
            exportArray.push({
              runtime,
              decoders: [decoder.currentStationData]
            })
          })
        } else if(step.decoders.length === 1) {
          exportArray.push({
            runtime,
            decoders: [step.decoders[0].stId]
          })
        }
      })
      return exportArray
    }
  },
  reload() {
    location.reload(true)
  },
}
</script>

<style lang="scss">
  $smallCol: 75px;

  .edit-program-steps {
    $parent: &;
    @include clearfix();
    overflow: hidden;
    display: flex;
    align-items: stretch;

    .developer-export-test {
      margin: 10px;
      float: right;
    }

    > * {
      max-width: 100%;
      flex: 0 0 100%;
    }

    &.show-edit-stations {
      .edit-program-stations {
        transform: translateX(-100%);
        transition: transform 0.4s;
      }
    }

    .mobile-only {
      display: inline-block;
    }

    small {
      font-size: 11px;
      margin-left: 15px;
    }

    h1.g-title {
      padding: 15px;
      font-size: 20px;
      line-height: 24px;
      font-family: $font-primary;
      margin-bottom: 0;
    }

    &__error {
      box-sizing: border-box;
      background: $color-white;
      padding: 10px 20px;
      max-width: 100vw;
      min-width: 200px;
      width: 100%;
      border-radius: $application-base-border-radius !important;
      line-height: 18px;

      p {
        margin-bottom: 0;
      }

      &__action {
        text-align: center;
      }

      .g-btn {
        margin: 20px auto;
      }
    }

    .step-list {
      text-align: center;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: 1fr;
      grid-gap: 5px;
      padding: 7px;

      .time-picker {
        display: inline-block;
        width: 100%;

        .display-time, .dropdown, .select-list {
          box-sizing: border-box;
          width: 100%;
        }

        * {
          box-sizing: border-box;
        }
      }

      &--header {
        display: none;
        padding-bottom: 0;
      }

      &--item:nth-child(odd) {
        background: lighten($color-grey, 20);
      }
      &--add-step {
        li {
          padding: 0 !important;
          width: 100%;
        }
        > li {
          &:first-child {
            display: none;
          }
          &:not(:first-child) {
            grid-column: 1 / span 3;
          }
        }
      }

      &__step {
        display: flex;
        align-items: center;
        justify-content: center;

        @at-root .edit-program-steps--single .step-list__step {
          &--step, &--flow, &--step-add-above {
            display: none !important;
          }
        }
        
        .g-btn--round > svg {
          transform: translate3d(-50%, -50%, 0);
        }

        &:first-child {
          padding: 0 20px;
        }
        &--title {

          &--small {
            font-size: 12px;
          }
        }

        &--step, &--flow, &--stations, &--runtime {
          grid-column: 1 / span 3;
        }

        &--stations, &--runtime {
          width: 100%;
        }

        &--runtime  {
          > span {
            margin-right: 20px;
          }
        }

        &__btn, .g-btn--station{
          width: 0;
          height: 0;
          padding: 20px;

          @at-root .edit-program-steps--single .step-list__step--stations {
            grid-column: 1 / span 3;
          }
      
          svg {
            transform: translate3d(-50%, -50%, 0);
          }
        }
        &__flow {
          background-color: $color-grey;
          width: 60px;
          color: $color-white;
          text-align: center;
          border-radius: 1.25em;
          padding: 10px;
          font-family: $font-primary-semibold;
          cursor: help;
          &--green {
            background-color: $color-green-ok;
          }
          &--yellow {
            background-color: $color-yellow-warn;
          }
          &--red {
            background-color: $color-red-error;
          }
          &--blue {
            background-color: $color-button-blue;
          }
        }

        &__stations {

          text-align: left;
          .g-btn--station {
            font-size: 12px !important; // @TODO: No !important
            .active & {
              color: $color-red-error !important;
            }
            &:first-child {
              margin-left: 10px;
            }
          }
        }
      }
    }
  }

  @media screen and (min-width: $bp-tablet) {
    .edit-program-steps {
      @include clearfix();

      .mobile-only {
        display: none;
      }

      .g-title {
        padding-left: 15px;
        font-size: 22px;
        font-family: $font-primary;
      }

      .timepicker-element {
        width: 100%;
      }

      .step-list {
        grid-template-columns: ($smallCol * 0.5) ($smallCol * 2.6) ($smallCol * 0.7) minmax(120px, 1fr) $smallCol $smallCol $smallCol;

        @at-root .edit-program-steps--single .step-list {
          grid-template-columns: ($smallCol * 2.6)  minmax(120px, 1fr) $smallCol $smallCol;
        }

        &__step {
          &--step, &--flow, &--stations, &--runtime {
            grid-column: auto;
          }

          &--stations, &--runtime {
            width: 100%;
          }
        }

        &--header {
          display: grid;
        }
        &--add-step {
          display: flex;
          align-items: center;
          li {
            display: block !important;
            margin: 0 0 0 7px;

            &:not(:first-child) {
              margin-left: 0;
            }

            button {
              margin-left: 0;
            }
          }
        }
        &__step {
          &--stations {
            grid-column: auto;
          }

        }
      }
    }
  }
</style>
